import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PendingAccountDeletion } from '@up2d8/contracts';
import { formatDate } from '../../shared/format';
import { useSession } from '../../shared/session';
import { Note } from '../admin-kit';

/**
 * Wie gevraagd heeft om zijn account te laten verwijderen (DEL-1, ADR-0098). Alleen lezen.
 *
 * Een beheerder hoeft niets te doen: het account staat al uit en wordt na de bedenktijd vanzelf
 * gewist. Hij moet het wel kunnen zien, anders lijkt het alsof een collega plots niet meer kan
 * aanmelden. Zonder verzoeken staat hier niets.
 */
export function PendingDeletionsNote() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const allowed = has('identity.account.manage');

  const pending = useQuery({
    queryKey: ['accounts', 'deletion-requests'],
    enabled: allowed,
    queryFn: async (): Promise<PendingAccountDeletion[]> => {
      const { data, error } = await api.GET('/api/v1/accounts/deletion-requests');
      if (error) throw error;
      return (data as { items: PendingAccountDeletion[] }).items;
    },
  });

  const items = pending.data ?? [];
  if (!allowed || items.length === 0) return null;

  return (
    <Note tone="attention" lead={t('accountDeletion.admin.title', { count: items.length })}>
      <p>{t('accountDeletion.admin.intro')}</p>
      <ul style={{ paddingLeft: '1.25rem', listStyle: 'disc' }}>
        {items.map((item) => (
          <li key={item.personId}>
            {t('accountDeletion.admin.row', {
              name: item.displayName,
              requested: formatDate(item.requestedAt),
              erase: formatDate(item.eraseAfter),
            })}
          </li>
        ))}
      </ul>
    </Note>
  );
}
